export const APPLICATION_STATUS_CONTENT = Object.freeze({
  draft: {
    label: 'Draft',
    message:
      'Your application has not been submitted. Continue when you are ready.',
  },
  submitted: {
    label: 'Submitted',
    message:
      'Your application is marked as submitted. No action is required right now. We’ll post updates here.',
  },
  in_review: {
    label: 'In review',
    message:
      'Your application is being reviewed. We’ll notify you if the programme team needs more information.',
  },
  more_information_required: {
    label: 'Action needed',
    message:
      'The programme team needs more information. Review the request and submit the additional details.',
  },
  shortlisted: {
    label: 'Shortlisted',
    message:
      'Your application has moved to the next stage. Review the next steps and any response deadline.',
  },
  approved: {
    label: 'Approved',
    message:
      'Your application has been approved. Complete the required onboarding before programme access or a research credential can be activated.',
  },
  selected: {
    label: 'Selected',
    message:
      'You have been selected for the fellowship. Confirm your place, then complete onboarding.',
  },
  not_selected: {
    label: 'Not selected',
    message:
      'Your application was not selected for this programme cycle. Thank you for the time and care you put into applying.',
  },
  withdrawn: {
    label: 'Withdrawn',
    message:
      'This application has been withdrawn and is no longer under review. Contact the programme team if this was unexpected.',
  },
});

export const ONBOARDING_STATUS_CONTENT = Object.freeze({
  not_started: {
    label: 'Not started',
    message: 'This task has not been started.',
  },
  in_progress: {
    label: 'In progress',
    message:
      'Your changes have been saved. Return when you are ready to continue.',
  },
  submitted: {
    label: 'Submitted',
    message: 'Submitted for programme review.',
  },
  complete: {
    label: 'Complete',
    message: 'Task completed.',
  },
  needs_changes: {
    label: 'Changes requested',
    message:
      'Review the programme team’s note and update this task.',
  },
  locked: {
    label: 'Locked',
    message: 'This task will become available after the required earlier step.',
  },
});

export const CREDENTIAL_STATUS_CONTENT = Object.freeze({
  not_issued: {
    label: 'Not issued',
    title: 'No credential has been issued',
    message:
      'A research credential becomes available only after approval and the required onboarding.',
  },
  pending: {
    label: 'Pending',
    title: 'Credential request under review',
    message: 'Your credential request is being reviewed.',
  },
  active: {
    label: 'Active',
    title: 'Research credential active',
    message: 'The credential is active until the date shown below.',
  },
  expired: {
    label: 'Expired',
    title: 'Research credential expired',
    message:
      'This credential has expired and can no longer be used for programme verification.',
  },
  revoked: {
    label: 'Revoked',
    title: 'Research credential no longer valid',
    message:
      'This credential is no longer valid. Contact the programme team for details.',
  },
});

export const PROGRAMME_SYSTEM_MESSAGES = Object.freeze({
  loading: 'Loading your programme information…',
  emptyUpdates:
    'No programme updates yet. Questions, decisions and next steps will appear here.',
  emptyResources: 'No resources have been published for you yet.',
  expiredSession: 'Your session has expired. Sign in again to continue.',
  forbiddenRecord:
    'You do not have access to this application or programme record.',
  staleUpdate:
    'This information was updated in another session. Review the latest version before continuing.',
  partialData:
    'Some programme information is temporarily unavailable. You can continue using the sections shown here.',
  temporaryFailure:
    'We couldn’t load this information right now. Try again.',
  applicationFailure:
    'We couldn’t submit your application. Your answers are still saved. Try again.',
  accessFailure:
    'We couldn’t complete this request right now. Try again.',
  uploadFailure:
    'We couldn’t upload this file. Check the permitted file type and size, then try again.',
});

export const normalizeProgrammeStatus = (status, fallback = 'submitted') => {
  const normalized = String(status || fallback)
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');

  return APPLICATION_STATUS_CONTENT[normalized] ? normalized : fallback;
};

export const getApplicationStatusContent = (status) => {
  const normalizedStatus = normalizeProgrammeStatus(status);
  return APPLICATION_STATUS_CONTENT[normalizedStatus];
};

export const getOnboardingStatus = (task) => {
  if (task?.status && ONBOARDING_STATUS_CONTENT[task.status]) {
    return task.status;
  }
  return task?.complete ? 'complete' : 'not_started';
};
